import { useAppStore } from '@store/appStore';
import { useState, useEffect } from 'react';
import './SettingsPanel.css';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SettingsPanel({ isOpen, onClose }: SettingsPanelProps) {
  const settings = useAppStore((state) => state.settings);
  const updateSettings = useAppStore((state) => state.updateSettings);
  const toggleDebugOverlay = useAppStore((state) => state.toggleDebugOverlay);
  const toggleMeshViewer = useAppStore((state) => state.toggleMeshViewer);
  const showDebugOverlay = useAppStore((state) => state.showDebugOverlay);
  const showMeshViewer = useAppStore((state) => state.showMeshViewer);
  
  // Phase 2: Pixel optimization state
  const hasPixelData = useAppStore((state) => state.hasPixelData);
  const pixelCacheStats = useAppStore((state) => state.pixelCacheStats);
  
  // Dataset management
  const availableDatasets = useAppStore((state) => state.availableDatasets);
  const currentDatasetId = useAppStore((state) => state.currentDatasetId);
  const loadDataset = useAppStore((state) => state.loadDataset);
  const isLoadingData = useAppStore((state) => state.isLoadingData);
  
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>(currentDatasetId || '');
  
  useEffect(() => {
    if (currentDatasetId) {
      setSelectedDatasetId(currentDatasetId);
    }
  }, [currentDatasetId]);
  
  const handleTransitionDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    updateSettings({ transitionDuration: value });
  };
  
  const handleSensitivityChange = (sensitivity: 'low' | 'medium' | 'high') => {
    updateSettings({ gestureSensitivity: sensitivity });
  };
  
  const handleDebugToggle = () => {
    toggleDebugOverlay();
  };

  const handleMeshViewerToggle = () => {
    toggleMeshViewer();
  };
  
  const handleRaycastModeChange = (mode: 'projection' | 'pixels' | 'auto') => {
    console.log(`⚙️  [SettingsPanel] Raycast mode changed:`, {
      from: settings.raycastMode,
      to: mode,
      hasPixelData,
      pixelCacheSize: pixelCacheStats?.cacheSize || 0
    })
    updateSettings({ raycastMode: mode });
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="settings-panel__backdrop" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Settings Panel */}
      <div className="settings-panel">
        <div className="settings-panel__header">
          <h2>Settings</h2>
          <button 
            className="settings-panel__close"
            onClick={onClose}
            aria-label="Close settings"
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        
        <div className="settings-panel__content">
          {/* Dataset Selector */}
          <div className="settings-panel__section">
            <label className="settings-panel__label">
              Dataset
            </label>
            {availableDatasets.length > 0 ? (
              <>
                <select
                  className="settings-panel__select"
                  value={selectedDatasetId}
                  onChange={(e) => setSelectedDatasetId(e.target.value)}
                  disabled={isLoadingData}
                >
                  <option value="">Select a dataset...</option>
                  {availableDatasets.map((dataset) => (
                    <option key={dataset.id} value={dataset.id}>
                      {dataset.name} ({dataset.camera_count} cameras, {dataset.success_rate})
                    </option>
                  ))}
                </select>
                <button
                  className="settings-panel__button"
                  onClick={() => selectedDatasetId && loadDataset(selectedDatasetId)}
                  disabled={isLoadingData || !selectedDatasetId || selectedDatasetId === currentDatasetId}
                >
                  {isLoadingData ? 'Loading...' : 'Load Dataset'}
                </button>
                {currentDatasetId && (
                  <p className="settings-panel__description">
                    Current: {availableDatasets.find(d => d.id === currentDatasetId)?.name}
                  </p>
                )}
              </>
            ) : (
              <p className="settings-panel__description">
                No datasets available. Check datasets.json manifest.
              </p>
            )}
          </div>
          
          {/* Transition Duration Slider */}
          <div className="settings-panel__section">
            <label className="settings-panel__label">
              Transition Duration
              <span className="settings-panel__value">{settings.transitionDuration}ms</span>
            </label>
            <input
              type="range"
              min="200"
              max="800"
              step="50"
              value={settings.transitionDuration}
              onChange={handleTransitionDurationChange}
              className="settings-panel__slider"
            />
            <div className="settings-panel__slider-labels">
              <span>Fast (200ms)</span>
              <span>Slow (800ms)</span>
            </div>
          </div>
          
          {/* Gesture Sensitivity */}
          <div className="settings-panel__section">
            <label className="settings-panel__label">
              Gesture Sensitivity
            </label>
            <div className="settings-panel__radio-group">
              <button
                className={`settings-panel__radio-button ${settings.gestureSensitivity === 'low' ? 'active' : ''}`}
                onClick={() => handleSensitivityChange('low')}
              >
                <div className="settings-panel__radio-circle">
                  {settings.gestureSensitivity === 'low' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>Low</span>
              </button>
              
              <button
                className={`settings-panel__radio-button ${settings.gestureSensitivity === 'medium' ? 'active' : ''}`}
                onClick={() => handleSensitivityChange('medium')}
              >
                <div className="settings-panel__radio-circle">
                  {settings.gestureSensitivity === 'medium' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>Medium</span>
              </button>
              
              <button
                className={`settings-panel__radio-button ${settings.gestureSensitivity === 'high' ? 'active' : ''}`}
                onClick={() => handleSensitivityChange('high')}
              >
                <div className="settings-panel__radio-circle">
                  {settings.gestureSensitivity === 'high' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>High</span>
              </button>
            </div>
            <p className="settings-panel__description">
              Higher sensitivity requires shorter swipe distances
            </p>
          </div>
          
          {/* Raycast Mode (Phase 2: Pixel Optimization) */}
          <div className="settings-panel__section">
            <label className="settings-panel__label">
              Raycast Method {hasPixelData && <span className="settings-panel__badge">v2.0</span>}
            </label>
            <div className="settings-panel__radio-group">
              <button
                className={`settings-panel__radio-button ${settings.raycastMode === 'projection' ? 'active' : ''}`}
                onClick={() => handleRaycastModeChange('projection')}
              >
                <div className="settings-panel__radio-circle">
                  {settings.raycastMode === 'projection' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>Projection</span>
              </button>
              
              <button
                className={`settings-panel__radio-button ${settings.raycastMode === 'pixels' ? 'active' : ''}`}
                onClick={() => handleRaycastModeChange('pixels')}
                disabled={!hasPixelData}
              >
                <div className="settings-panel__radio-circle">
                  {settings.raycastMode === 'pixels' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>Pixels {!hasPixelData && '(N/A)'}</span>
              </button>
              
              <button
                className={`settings-panel__radio-button ${settings.raycastMode === 'auto' ? 'active' : ''}`}
                onClick={() => handleRaycastModeChange('auto')}
              >
                <div className="settings-panel__radio-circle">
                  {settings.raycastMode === 'auto' && (
                    <div className="settings-panel__radio-dot" />
                  )}
                </div>
                <span>Auto</span>
              </button>
            </div>
            <p className="settings-panel__description">
              {settings.raycastMode === 'projection' && 'Standard 3D projection (slower, ~50ms)'}
              {settings.raycastMode === 'pixels' && hasPixelData && `COLMAP pixel lookup (faster, <5ms) - Cache: ${pixelCacheStats?.cacheSize || 0} points`}
              {settings.raycastMode === 'pixels' && !hasPixelData && 'Requires v2.0 dataset with pixel data'}
              {settings.raycastMode === 'auto' && 'Use pixels if available, fallback to projection'}
            </p>
            {pixelCacheStats && settings.raycastMode !== 'projection' && (
              <div className="settings-panel__stats">
                <div className="settings-panel__stat">
                  <span className="settings-panel__stat-label">Cache Size:</span>
                  <span className="settings-panel__stat-value">{pixelCacheStats.cacheSize} points</span>
                </div>
                <div className="settings-panel__stat">
                  <span className="settings-panel__stat-label">Build Time:</span>
                  <span className="settings-panel__stat-value">{pixelCacheStats.buildTime.toFixed(2)}ms</span>
                </div>
                <div className="settings-panel__stat">
                  <span className="settings-panel__stat-label">Hit Rate:</span>
                  <span className="settings-panel__stat-value">
                    {((pixelCacheStats.hits / Math.max(1, pixelCacheStats.hits + pixelCacheStats.misses)) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Mesh Viewer Toggle */}
          <div className="settings-panel__section">
            <label className="settings-panel__label">
              3D Mesh Viewer
            </label>
            <button
              className={`settings-panel__toggle ${showMeshViewer ? 'active' : ''}`}
              onClick={handleMeshViewerToggle}
              aria-pressed={showMeshViewer}
            >
              <div className="settings-panel__toggle-track">
                <div className="settings-panel__toggle-thumb" />
              </div>
              <span>{showMeshViewer ? 'On' : 'Off'}</span>
            </button>
            <p className="settings-panel__description">
              Display 3D mesh generated from COLMAP point cloud
            </p>
          </div>
          
          {/* Advanced Settings - Collapsible */}
          <details className="settings-panel__section settings-panel__advanced">
            <summary className="settings-panel__label settings-panel__summary">
              ⚙️ Advanced Navigation Settings
            </summary>
            
            <div className="settings-panel__advanced-content">
              {/* DZ Filter Toggle */}
              <div className="settings-panel__subsection">
                <label className="settings-panel__label">
                  Depth Change Filter (dz)
                  <span className="settings-panel__badge-new">NEW</span>
                </label>
                <button
                  className={`settings-panel__toggle ${settings.enableDzFilter ? 'active' : ''}`}
                  onClick={() => updateSettings({ enableDzFilter: !settings.enableDzFilter })}
                  aria-pressed={settings.enableDzFilter}
                >
                  <div className="settings-panel__toggle-track">
                    <div className="settings-panel__toggle-thumb" />
                  </div>
                  <span>{settings.enableDzFilter ? 'On' : 'Off'}</span>
                </button>
                <p className="settings-panel__description">
                  {settings.enableDzFilter 
                    ? '✅ Prevents jumping to frames with large depth changes (scene changes blocked)'
                    : '⚠️ Allows navigation to any frame (may jump between different scenes)'}
                </p>
              </div>
              
              {/* Frame Blur Toggle */}
              <div className="settings-panel__subsection">
                <label className="settings-panel__label">
                  Background Frame Blur
                </label>
                <button
                  className={`settings-panel__toggle ${settings.enableFrameBlur ? 'active' : ''}`}
                  onClick={() => updateSettings({ enableFrameBlur: !settings.enableFrameBlur })}
                  aria-pressed={settings.enableFrameBlur}
                >
                  <div className="settings-panel__toggle-track">
                    <div className="settings-panel__toggle-thumb" />
                  </div>
                  <span>{settings.enableFrameBlur ? 'On' : 'Off'}</span>
                </button>
                <p className="settings-panel__description">
                  Blur background frames for depth-of-field effect
                </p>
              </div>
              
              {/* Distance Coverage Toggle */}
              <div className="settings-panel__subsection">
                <label className="settings-panel__label">
                  Distance-Based Coverage
                </label>
                <button
                  className={`settings-panel__toggle ${settings.useDistanceCoverage ? 'active' : ''}`}
                  onClick={() => updateSettings({ useDistanceCoverage: !settings.useDistanceCoverage })}
                  aria-pressed={settings.useDistanceCoverage}
                >
                  <div className="settings-panel__toggle-track">
                    <div className="settings-panel__toggle-thumb" />
                  </div>
                  <span>{settings.useDistanceCoverage ? 'On' : 'Off'}</span>
                </button>
                <p className="settings-panel__description">
                  Use Euclidean distance instead of viewport intersection
                </p>
              </div>
              
              {/* Stage 2 Intelligent Selection Toggle */}
              <div className="settings-panel__subsection">
                <label className="settings-panel__label">
                  Stage 2 Intelligent Selection
                  <span className="settings-panel__badge-new">EXPERIMENTAL</span>
                </label>
                <button
                  className={`settings-panel__toggle ${!settings.useOldPanBehavior ? 'active' : ''}`}
                  onClick={() => updateSettings({ useOldPanBehavior: !settings.useOldPanBehavior })}
                  aria-pressed={!settings.useOldPanBehavior}
                >
                  <div className="settings-panel__toggle-track">
                    <div className="settings-panel__toggle-thumb" />
                  </div>
                  <span>{!settings.useOldPanBehavior ? 'On (Stage 2)' : 'Off (Coverage)'}</span>
                </button>
                <p className="settings-panel__description">
                  {!settings.useOldPanBehavior 
                    ? '✨ Stage 2: Uses pan direction, angle, and 3D geometry for smarter frame selection'
                    : '📏 Coverage Mode: Selects frame with highest viewport coverage (classic behavior)'}
                </p>
              </div>
            </div>
          </details>
          
          {/* Info Section */}
          <div className="settings-panel__section settings-panel__info">
            <h3>About</h3>
            <p>
              Gesture-based navigation for COLMAP 3D reconstructions.
              Use swipe gestures to explore shop environments in an immersive way.
            </p>
            <p className="settings-panel__version">
              Version 1.0.0 MVP
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
